/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * QuestionDetailsCard
 *
 * @file  QuestionDetailsCard.jsx
 * @since 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import MathTextComponent from '../common/MathTextComponent';
import {
  ChapterHeader,
  CorrectLabel,
  CorrectOption,
  CorrectSubLabel,
  FlagButton,
  FlagIcon,
  HeaderActions,
  HeaderLeft,
  HeaderRow,
  OptionContent,
  OptionIcon,
  OptionLabel,
  OptionPrefix,
  OptionRow,
  Options,
  PrefixCorrect,
  PrefixWrong,
  PrimaryCol1,
  QuestionNumberWrapper,
  QuestionStem,
  QuestionTitle,
  ReviewBadge,
  ReviewButton,
  Root,
  SelectedOptions,
  WrongLabel,
  WrongSubLabel
} from './styles/DifficultTopicStyles';
import Icons from '../icons/Icon';
import ReviewQuestionModal from './ReportQuestionModal';
import { getDataAttributes, dataAttributesPropType } from '../../common/dataAttributes';

const getOptionRowComponents = (isCorrect, isSelected) => {
  if (isCorrect) {
    return {
      RowComponent: CorrectOption,
      PrefixComponent: PrefixCorrect,
      LabelComponent: CorrectLabel
    };
  }
  if (isSelected) {
    return {
      RowComponent: SelectedOptions,
      PrefixComponent: PrefixWrong,
      LabelComponent: WrongLabel
    };
  }

  return {
    RowComponent: OptionRow,
    PrefixComponent: OptionPrefix,
    LabelComponent: OptionLabel
  };
};

const QuestionDetailsCard = (props) => {
  const {
    questions,
    onFlaggedQuestionClick,
    onReviewItClick,
    onClose,
    open,
    onInaccurateClick,
    difficultTopicReviewData,
    reportQuestionReasons,
    reviewCount = 0,
    components,
    classes: customClasses = {},
    reviewButtonDataAttributes,
    ...otherProps
  } = props;
  const questionTitleClassName = customClasses?.questionTitle;
  const chapterHeaderClassName = customClasses?.chapterHeader;
  const reviewButtonClassName = customClasses?.reviewButton;

  if (!questions) return null;

  const {
    chapterTitle,
    question_stem: questionStem,
    questionNumber,
    options = [],
    isFlagged
  } = questions;

  const getLetter = (index) => String.fromCharCode(65 + index);

  return (
    <>
      <Root component="section">
        <HeaderRow>
          <HeaderLeft>
            <MathTextComponent
              text={chapterTitle}
              component={ChapterHeader}
              variant="subtitle2"
              className={chapterHeaderClassName}
            />
          </HeaderLeft>
          <HeaderActions>
            {isFlagged && (
              <FlagButton component="span">
                <FlagIcon>
                  <Icons name="FlagIcon" />
                </FlagIcon>
                {otherProps.textInfo.flaggedQuestionLabel}
              </FlagButton>
            )}

            {otherProps.textInfo.reviewItLabel && (
              <ReviewBadge
                badgeContent={reviewCount}
                invisible={reviewCount === 0}
                color="secondary"
                overlap="rectangular"
              >
                <ReviewButton
                  variant="contained"
                  onClick={onReviewItClick}
                  className={reviewButtonClassName}
                  {...getDataAttributes(reviewButtonDataAttributes)}
                >
                  {otherProps.textInfo.reviewItLabel}
                </ReviewButton>
              </ReviewBadge>
            )}
          </HeaderActions>
        </HeaderRow>
        {questionStem && (
          <QuestionStem>
            {questionNumber && (
              <QuestionNumberWrapper>
                <QuestionTitle className={questionTitleClassName}>{questionNumber}</QuestionTitle>
                <QuestionTitle className={questionTitleClassName} component="span">:</QuestionTitle>
              </QuestionNumberWrapper>
            )}
            <PrimaryCol1>
              <MathTextComponent
                text={questionStem}
                className={questionTitleClassName}
                component="p"
              />
            </PrimaryCol1>
          </QuestionStem>
        )}

        <Options>
          {options.map((opt, index) => {
            const isCorrect = opt.correct_choice === 'Yes';
            const isSelected = opt.is_most_common_incorrect_response === true;

            const {
              RowComponent,
              PrefixComponent,
              LabelComponent
            } = getOptionRowComponents(isCorrect, isSelected);

            return (
              <RowComponent key={opt.id}>
                <PrefixComponent component="span">
                  {getLetter(index)}
                </PrefixComponent>

                <OptionContent>
                  <MathTextComponent
                    text={opt.distractor || ''}
                    component={LabelComponent}
                  />

                  {isCorrect && (
                    <CorrectSubLabel>
                      {otherProps.textInfo.correctAnswer}
                    </CorrectSubLabel>
                  )}

                  {!isCorrect && isSelected && (
                    <WrongSubLabel>
                      {otherProps.textInfo.incorrectAnswer}
                    </WrongSubLabel>
                  )}
                </OptionContent>

                {isCorrect && (
                  <OptionIcon>
                    <Icons name="PositiveIcon" />
                  </OptionIcon>
                )}
                {!isCorrect && isSelected && (
                  <OptionIcon>
                    <Icons name="NegativeIcon" />
                  </OptionIcon>
                )}
              </RowComponent>
            );
          })}
        </Options>

        {components?.viewQuestions ? (
          <div>
            {components.viewQuestions}
          </div>
        ) : null}
      </Root>
      <ReviewQuestionModal
        onClose={onClose}
        open={open}
        reviewData={difficultTopicReviewData}
        onInaccurateClick={onInaccurateClick}
        textInfo={otherProps.textInfo}
        reportQuestionReasons={reportQuestionReasons}
      />
    </>
  );
};

QuestionDetailsCard.propTypes = {
  questions: PropTypes.shape({
    chapterTitle: PropTypes.string,
    question_stem: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    isFlagged: PropTypes.bool,
    questionNumber: PropTypes.string
  }).isRequired,
  onFlaggedQuestionClick: PropTypes.func,
  onReviewItClick: PropTypes.func,
  reviewButtonDataAttributes: dataAttributesPropType,
  textInfo: PropTypes.shape({
    flaggedQuestionLabel: PropTypes.string,
    reviewItLabel: PropTypes.string,
    correctAnswer: PropTypes.string,
    incorrectAnswer: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  onInaccurateClick: PropTypes.func,
  difficultTopicReviewData: PropTypes.object,
  reportQuestionReasons: PropTypes.array,
  reviewCount: PropTypes.number,
  components: PropTypes.shape({
    viewQuestions: PropTypes.node
  }),
  classes: PropTypes.object
};

QuestionDetailsCard.defaultProps = {
  reviewButtonDataAttributes: {},
  onFlaggedQuestionClick: () => {},
  onReviewItClick: () => {},
  onClose: () => {},
  open: false,
  onInaccurateClick: () => {},
  difficultTopicReviewData: {},
  reportQuestionReasons: [],
  reviewCount: 0,
  components: null,
  classes: {}
};

export default QuestionDetailsCard;
